<template>
  <div
    class="kd-foto-layer"
    :class="{
      'kd-foto-layer--empty': !imageIsLoaded
    }"
    @dragstart.prevent.stop="/* prevent dragging of image in Firefox */"
    @mousedown.prevent.stop="onImageMouseDown($event)"
    @mouseup.prevent.stop="onImageMouseUp($event)"
    @mousemove.prevent.stop="onImageMouseMove($event)"
    @touchstart.prevent.stop="onImageTouchStart($event)"
    @touchmove.prevent.stop="onImageTouchMove($event)"
    @touchend.prevent.stop="onImageTouchEnd($event)"
    v-tooltip="{
      content: tooltipText,
      show: showTooltip,
      trigger: 'manual',
      placement: tooltipPlacement,
    }"
    :disabled="false"
  >
    <img
      v-show="imageIsLoaded"
      class="kd-foto-layer__image"
      :src="fullImageUrl"
      :style="imageStyle"
      ref="image"
      @loadstart="imageLoadingStarted"
      @load="imageLoaded"
    >
    <kd-image-quality-indicator
      v-if="imageIsLoaded"
      :dpi="dpi"
    ></kd-image-quality-indicator>
  </div>
</template>

<script>
import * as log from 'loglevel';
import { mapActions, mapGetters } from 'vuex';

import KdImageQualityIndicator from '@/components/KdImageQualityIndicator.vue';

import {
  ACTIVE_LAYER_ID,
  TOOLTIPS_VISIBLE,
  LAYER_IS_FIRST_OF_TYPE,
} from '@/store/getter-types';

import {
  SET_TRANSLATE_X_FOR_ACTIVE_LAYER,
  SET_TRANSLATE_Y_FOR_ACTIVE_LAYER,
} from '@/store/mutation-types';

import {
  ACTIVATE_LAYER_BY_ID,
} from '@/store/action-types';

import tooltipTexts from '@/store/tooltip-texts';

// TODO: Move into external file, together with min/max for slider / settings component
const DEFAULT_FILTER_BRIGHTNESS_VALUE = 1;
const DEFAULT_FILTER_CONTRAST_VALUE = 1;
const DEFAULT_SCALE = 1;
const DEFAULT_TRANSLATE_X = 0;
const DEFAULT_TRANSLATE_Y = 0;

export default {
  props: {
    layer: Object,
  },
  data() {
    return {
      imageIsLoaded: false,
      imageBeingDragged: false,
      tooltipPlacement: 'left',
      previousTouchX: 0,
      previousTouchY: 0,
      previousMouseX: 0, // needed for IE11 (does not support ev.movementX)
      previousMouseY: 0, // needed for IE11 (does not support ev.movementY)
    };
  },
  computed: {
    ...mapGetters([
      ACTIVE_LAYER_ID,
      TOOLTIPS_VISIBLE,
      LAYER_IS_FIRST_OF_TYPE,
    ]),
    imageUrl() {
      if (this.layer && this.layer.imageUrl) {
        return this.layer.imageUrl;
      }
      return '';
    },
    fullImageUrl() {
      if (this.imageUrl) {
        // -> xyz.hach.de/uploads/1234.svg / localhost:3000/uploads/1234.svg
        return process.env.VUE_APP_IMAGE_SERVER + this.imageUrl;
      }
      return '';
    },
    /* eslint-disable no-prototype-builtins */
    filterCss() {
      let filterCss = '';
      if (this.layer.hasOwnProperty('filterBrightness')
          && this.layer.filterBrightness !== DEFAULT_FILTER_BRIGHTNESS_VALUE) {
        filterCss += ` brightness(${this.layer.filterBrightness}) `;
      }
      if (this.layer.hasOwnProperty('filterContrast')
          && this.layer.filterContrast !== DEFAULT_FILTER_CONTRAST_VALUE) {
        filterCss += ` contrast(${this.layer.filterContrast}) `;
      }
      if (this.layer.filterSepiaActive) { filterCss += ' sepia(1) '; }
      if (this.layer.filterGrayscaleActive) { filterCss += ' grayscale(1) '; }
      return filterCss;
    },
    /* eslint-disable no-prototype-builtins */
    transformCss() {
      if (!this.layer) { return ''; }
      let transformCss = '';

      // ATTENTION: Using mm here might leed to problems, because inside the
      // card we normally use %. But as this the outer div (card) is specified
      // in mm it should be okay.
      // Using px here would probably be more problematic (because mm / px mix)
      if (this.layer.hasOwnProperty('transformTranslateX')
          && this.layer.transformTranslateX !== DEFAULT_TRANSLATE_X) {
        transformCss += ` translateX(${this.layer.transformTranslateX}mm) `;
      }
      if (this.layer.hasOwnProperty('transformTranslateY')
          && this.layer.transformTranslateY !== DEFAULT_TRANSLATE_Y) {
        transformCss += ` translateY(${this.layer.transformTranslateY}mm) `;
      }
      if (this.layer.hasOwnProperty('transformScale')
          && this.layer.transformScale !== DEFAULT_SCALE) {
        transformCss += ` scale(${this.layer.transformScale}) `;
      }
      if (this.layer.hasOwnProperty('transformRotate')
          && this.layer.transformRotate !== DEFAULT_SCALE) {
        transformCss += ` rotate(${this.layer.transformRotate}deg) `;
      }
      return transformCss;
    },
    translateX() {
      /* eslint-disable no-prototype-builtins */
      if (this.layer && this.layer.hasOwnProperty('transformTranslateX')) {
        return this.layer.transformTranslateX;
      }
      return 0;
    },
    /* eslint-disable no-prototype-builtins */
    translateY() {
      if (this.layer && this.layer.hasOwnProperty('transformTranslateY')) {
        return this.layer.transformTranslateY;
      }
      return 0;
    },
    imageStyle() {
      return {
        filter: this.filterCss,
        transform: this.transformCss,
      };
    },
    imageScale() {
      if (this.layer.hasOwnProperty('transformScale')) {
        return this.layer.transformScale;
      }
      return DEFAULT_SCALE;
    },
    /**
     * @returns 
     *   - image does not exist:       -1
     *   - image is a vector:        1200
     *   - otherwise:                 DPI
     */
    dpi() {
      // TODO: Check if is pixel image or not
      const isPixelImage = this.isPixelImage(this.imageUrl);
      if (!isPixelImage) { return 1200; }  // return 1200 dpi as indicator for "enough" DPI
      const img = this.$refs.image;
      if (!img) { return -1 };
      // To find out the size of the image in inch, we divide it by 96,
      // this is how the browser converts inch to pixels.
      // To make sure we could also create a hidden div here with width="1in",
      // height="1in" and check its width and height to have the 100% correct
      // conversion factor
      const widthInIn = img.width / 96;
      const inverseImgScale = 1 / this.imageScale;
      const dpi = img.naturalWidth / widthInIn * inverseImgScale;
      return dpi;
    },
    showTooltip() {
      return this.TOOLTIPS_VISIBLE && this.LAYER_IS_FIRST_OF_TYPE(this.layer.id, this.layer.type);
    },
    tooltipText: () => tooltipTexts.imageLayer,
  },
  methods: {
    ...mapActions([
      ACTIVATE_LAYER_BY_ID,
    ]),
    imageLoadingStarted() {
      this.imageIsLoaded = false;
    },
    imageLoaded() {
      this.imageIsLoaded = true;
    },
    /**
     * Checks if an image is a pixel image (instead of vector) by its URL.
     * '/uploads/foo.jpg' —> true
     * TODO: Maybe move externally?
     */
    isPixelImage(imageUrl) {
      if (!imageUrl) { return false; }
      const imageUrlLower = imageUrl.toLowerCase();
      const lastDotIndex = imageUrlLower.lastIndexOf('.');
      if (lastDotIndex === -1 || lastDotIndex === imageUrlLower.length - 1) {
        return false;
      }
      const suffix = imageUrlLower.substring(lastDotIndex + 1);
      suffix.trim();
      return ['png', 'jpg', 'jpeg', 'bmp', 'gif'].includes(suffix);
    },

    onImageMouseDown(ev) {
      this.ACTIVATE_LAYER_BY_ID(this.layer.id);
      this.imageBeingDragged = true;
    },
    /**
     * Support okay, see https://caniuse.com/#search=movementX
     */
    onImageMouseMove(ev) {
      // check if store action to activate layer has not completed yet
      // we must get the layerId from this variable to prevent dragging
      // another image when dragging over multiple containers
      if (!(this.layer && this.layer.id === this.ACTIVE_LAYER_ID)) { return; }
      if (this.imageBeingDragged) {
        // IE11 does not have the properties ev.movementX / ev.movementY,
        // so we must calculate the difference ourselves.
        if (typeof ev.movementX == 'undefined') {
          ev.movementX = (this.previousMouseX ? ev.screenX - this.previousMouseX : 0)
        }
        if (typeof ev.movementY == 'undefined') {
          ev.movementY = (this.previousMouseY ? ev.screenY - this.previousMouseY : 0)
        }
        
        const transformX = this.translateX + ev.movementX / 3;
        const transformY = this.translateY + ev.movementY / 3;
        this.$store.commit(SET_TRANSLATE_X_FOR_ACTIVE_LAYER, transformX);
        this.$store.commit(SET_TRANSLATE_Y_FOR_ACTIVE_LAYER, transformY);
        // store mouse position for next time
        this.previousMouseX = ev.screenX;
        this.previousMouseY = ev.screenY;
      }
    },
    onImageMouseUp(ev, layerId) {
      this.imageBeingDragged = false;
    },
    onImageTouchStart(ev) {
      this.ACTIVATE_LAYER_BY_ID(this.layer.id);
      this.imageBeingDragged = true;
      this.previousTouchX = ev.touches[0].clientX;
      this.previousTouchY = ev.touches[0].clientY;
    },
    /**
     * Support okay, see https://caniuse.com/#search=movementX
     */
    onImageTouchMove(ev) {
      // check if store action to activate layer has not completed yet
      if (!(this.layer && this.layer.id === this.ACTIVE_LAYER_ID)) { return; }
      if (this.imageBeingDragged) {
        // we must get the layerId from this variable to prevent dragging
        // another image when dragging over multiple containers
        const touchX = ev.touches[0].clientX;
        const touchY = ev.touches[0].clientY;
        const transformX = this.translateX + (touchX - this.previousTouchX) / 5;
        const transformY = this.translateY + (touchY - this.previousTouchY) / 5;
        this.$store.commit(SET_TRANSLATE_X_FOR_ACTIVE_LAYER, transformX);
        this.$store.commit(SET_TRANSLATE_Y_FOR_ACTIVE_LAYER, transformY);
        this.previousTouchX = touchX;
        this.previousTouchY = touchY;
      }
    },
    onImageTouchEnd(ev, layerId) {
      this.imageBeingDragged = false;
      this.previousTouchX = 0;
      this.previousTouchY = 0;
    },
    // preventDefault()

    /**
     * isOverlapping() returns the overlapping status between two elements
     * based on the passed in Element objects
     *
     * @param {Element, Element} Element object of DOM
     * @return {Boolean|null} overlap status or null if native objet not received
     */
    // isOverlapping(e1, e2){
    //   if( e1.length && e1.length > 1 ){
    //     e1 = e1[0];
    //   }
    //   if( e2.length && e2.length > 1 ){
    //     e2 = e2[0];
    //   }
    //   var rect1 = e1 instanceof Element ? e1.getBoundingClientRect() : false;
    //   var rect2 = e2 instanceof Element ? e2.getBoundingClientRect() : false;
      
    //   var overlap = null;
    //   if(rect1 && rect2){
    //     overlap = !(
    //         rect1.right < rect2.left || 
    //         rect1.left > rect2.right || 
    //         rect1.bottom < rect2.top || 
    //         rect1.top > rect2.bottom
    //       )
    //     return overlap;  
    //   } else {
    //     log.warn('Need native DOM elements as params');
    //     return overlap;
    //   }
    // }

    /**
     * Returns the DPI of the image element.
     * @param {Number} containerWidth in mm
     * @param {Number} containerHeight in mm
     */
    // getDpi(imageEl, containerWidth, containerHeight) {},
  },
  watch: {
    imageUrl(fn) {
      if (fn) {
        this.imageIsLoaded = true;
      }
    },
    // TOOLTIPS_VISIBLE(isVisible) {
    //   if (!this.layer || !this.LAYER_IS_FIRST_OF_TYPE(this.layer.id, 'foto')) { return; }
    //   if (isVisible) {
    //     const tooltips = document.querySelectorAll('.tooltip');
    //     if (tooltips && tooltips.length >= 2) {
    //       // Naive way to find out if tooltips are overlapping. Just checks if ANY tooltips are oberlapping
    //       // and changes the tooltip placement of the foto tooltip then.
    //       // This can be improved, so that each tooltip has an ID and the placement can
    //       // be set for that specific tooltip. Then we can really make sure that no tooltips
    //       // are overlapping.
    //       for (let i = 0; i < tooltips.length; i++) {
    //         for (let j = i + 1; j < tooltips.length; j++) {
    //           if (this.isOverlapping(tooltips[i], tooltips[j])) {
    //             // Let’s just naively assume the overlapping tooltips are the text layer
    //             // tooltip and the foto layer tooltip and re-position the foto-layer tooltip (this one)
    //             // this.tooltipPlacement = 'right';
    //             // manually updating the placement does not work... 
    //           }
    //         }
    //       }
    //     }
    //     // this.tooltipPlacement = 'left';
    //   }
    // },
  },
  components: {
    KdImageQualityIndicator,
  },
};
</script>

<style lang="scss">
@import '@/scss/kd-layer-settings-unit.scss';

.kd-foto-layer {
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.2);
  overflow: hidden;

  &--empty {
    background-color: rgba(255, 255, 255, 0.9);
    position: relative;
    cursor: pointer;

    &::after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      content: "";
      background-image: url('../assets/images/icons/image-upload-placeholder.svg');
      background-size: contain;
      background-position: center center;
      background-repeat: no-repeat;

      .kd--print & {
        display: none !important;
      }
    }
  }

  .kd--print & {
    background-color: transparent !important; // hide background when printing
  }
}

.kd-foto-layer__image {
  width: 100%;
  transform-origin: center center;
  position: relative;
  transform-origin: center center;
  cursor: move;
}
</style>
